import * as React from 'react';
import { AccessRolesInputControl, IRolesInputControlResult } from './AccessRolesInputControl';
import { IAccessRelationRawData, IAccessRelationData, IRolePerrmission } from './AccessDomainModels';
import { OptionType, IEmployeesFilterParams } from '../EmployeesComboboxMI';
import UserAccessesWindow, { IUserAccessesWindowConfig } from './UserAccessesWindow';

import { IEmployeeEntity, RequestControl } from '../Relations/RequestControl';
import { IUserAccessDataRow, IDictionary } from './AccessUserAccessesService';
import { AccessCheckExcludedRolePermissions } from './AccessCheckExcludedRolePermissions';
import { IConfig } from './IModels';
import ResourcesAutocompleteMI from '../ResourcesAutocompleteMI';
import lang from "../Relations/RelationsLang";
import WarningEmployeeChange from '../Relations/WarningEmployeeChange';
import { createGridConfig, IGridResourcesConfig } from '../Relations/GridResourcesConfig';
import { SensitiveDataAddScopeDialog } from './HtmlEditor/SensitiveDataAddScopeDialog';
import { IIssue } from '../Relations/IssueForRelations';
import { RenderMultiEmployeesInput } from '../Relations/RenderMultiEmployeesInput';
import { ErrorMessageWithLabel } from '../Relations/Components';
import UserAuthorizationsPreviewWindow, { IUserAuthorizationsPreviewWindowConfig } from './UserAuthorizationsPreviewWindow';

const defaultApplicationGrid: IGridResourcesConfig = {
	title: lang.selectApplication(),
	hidden: false,
	columns: [
		{ id: "name", label: lang.name(), minWidth: 210, searched: true, numeric: false },
		{ id: "applicationName", label: lang.applicationName(), searched: true, numeric: false },
		{ id: "type", label: lang.type(), searched: false, numeric: false },
		{ id: "status", label: lang.status(), searched: true, numeric: false },
		{ id: "city", label: lang.city(), searched: false, numeric: false },
		{ id: "company", label: lang.company(), searched: false, numeric: false },
		{ id: "department", label: lang.department(), searched: false, numeric: false }
	],
	filters: "",
	rowsPerPageOptions: null,
}

const defaultServiceGrid: IGridResourcesConfig = {
	title: lang.selectService(),
	hidden: false,
	columns: [
		{ id: "name", label: lang.name(), minWidth: 210, searched: true, numeric: false },
		{ id: "type", label: lang.type(), searched: false, numeric: false },
		{ id: "status", label: lang.status(), searched: true, numeric: false },
		{ id: "city", label: lang.city(), searched: false, numeric: false },
		{ id: "company", label: lang.company(), searched: false, numeric: false },
		{ id: "department", label: lang.department(), searched: false, numeric: false }
	],
	filters: "",
	rowsPerPageOptions: null,
}

interface IProps {
	issue: IIssue;

	blockInsert: boolean;
	
	selectedEmployees: OptionType[],
	onChangeSelectedEmployees: (v: OptionType[]) => void
	employeesFilters: IEmployeesFilterParams,

	selectedApplications: OptionType[],
	onChangeSelectedApplications: (v: OptionType[]) => void

	selectedServices: OptionType[],
	onChangeSelectedServices: (v: OptionType[]) => void

	table: RequestControl;

	checkAccessesInLog: (table: any[]) => void;

	rowsPerUsers: IDictionary<IUserAccessDataRow[]>,
	loading: IDictionary<boolean>,

	blockChangeEmployees: boolean;

	hiddenCurrentAccessesButton: boolean;
	currentAccessesTable: IUserAccessesWindowConfig,

	currentAuthorizationsButton: IUserAuthorizationsPreviewWindowConfig;

	config: IConfig,
}

interface IState {
	
	employeesError: string;
	resourcesError: string;
	
	warningEmployeeChange: OptionType;
	hasChanges: boolean;

	rowsWithSensitiveData: (IAccessRelationRawData | IAccessRelationData)[],
}

export default class AccessInsertForm extends React.Component<IProps, IState> {
	constructor(props) {
		super(props);

		this.state = {
			employeesError: "",
			resourcesError: "",
			warningEmployeeChange: null,
			hasChanges: false,
			rowsWithSensitiveData: [],
		}

		this.rolesInputs = new Map();
	}

	rolesInputs: Map<string, AccessRolesInputControl>;

	public hasChanges(): boolean {

		if (this.props.selectedApplications.length == 0 &&
			this.props.selectedServices.length == 0)
			return false;

		if (this.state.hasChanges)
			return true;

		return false;
	}

	renderRolesInput()
	{
		return <div className="access-roles-input-root">
			{this.props.selectedApplications.map((app) => {
				return <AccessRolesInputControl
					key={app.value}
					label={lang.getDefaultByPrefix(this.props.config.fields.application, "label", "module")}
					resource={app}
					resourceKind="Application"
					ref={c => this.rolesInputs.set("" + app.value, c)}

					config={this.props.config}
					selectedEmployees={this.props.selectedEmployees}
				/>
			})}

			{this.props.selectedServices.map((service) => {
				return <AccessRolesInputControl
					key={service.value}
					label={lang.getDefaultByPrefix(this.props.config.fields.service, "label", "service")}
					resource={service}
					resourceKind="ServiceAsset"
					ref={c => this.rolesInputs.set("" + service.value, c)}
					config={this.props.config}
					selectedEmployees={this.props.selectedEmployees}
				/>
			})}
		</div>
	}

	handleAddClick() {
		let distinctOptions: IDictionary<OptionType> = {};
		for (var i = 0; i <= this.props.selectedEmployees.length; i++) {
			var emp = this.props.selectedEmployees[i];
			if (emp && !distinctOptions[emp.value]) {
				distinctOptions[emp.value] = emp;
			}
		}

		if (this.isValidAdd(distinctOptions)) {
			
			let itemsToPush: IAccessRelationRawData[] = [];

			let uniqueEmployees = Object.values(distinctOptions) as OptionType[];
			
			if (uniqueEmployees.length == 0) {
				itemsToPush = this.createAccessesByUser(null);
			}

			for (let i = 0; i < uniqueEmployees.length; i++) {
				let employeeEntity: IEmployeeEntity = null;
				employeeEntity = {
					id: parseInt(uniqueEmployees[i].value),
					name: uniqueEmployees[i].label,
					kind: uniqueEmployees[i].kind,
					label: uniqueEmployees[i].label,
					type: null,
				};
				var newRows = this.createAccessesByUser(employeeEntity);
				
				let currentUserAccesses: IUserAccessDataRow[] = this.props.rowsPerUsers[employeeEntity.id];
				
				for (var j = 0; j < newRows.length; j++) {
					var role = newRows[j].role;
					newRows[j].excluded = new AccessCheckExcludedRolePermissions(this.props.table, currentUserAccesses)
						.checkExcludedRolePermissions(role, itemsToPush);
				}

				itemsToPush = itemsToPush.concat(newRows);
			}

			this.checkAndpushItems(itemsToPush);
		}
		return false;
	}

	private checkAndpushItems(itemsToPush: IAccessRelationRawData[]) {
		let sensitiveData = itemsToPush.filter(x => this.openSensitiveDataDialog(x.role));
		let withoutSensitiveData = itemsToPush.filter(x => !this.openSensitiveDataDialog(x.role));

		this.pushItems(withoutSensitiveData);
		this.clear();
		this.setState({ rowsWithSensitiveData: sensitiveData });
	}

	private pushItems(itemsToPush: IAccessRelationRawData[]) {
		
		this.props.checkAccessesInLog(itemsToPush);
		this.props.table.push(itemsToPush);
	}

	private createAccessesByUser(employeeEntity: null | IEmployeeEntity): IAccessRelationRawData[] {
		let itemsToPush: IAccessRelationRawData[] = [];

		for (let j = 0; j < this.props.selectedApplications.length; j++) {
			let module = this.props.selectedApplications[j];
			let addModules = this.createAccessByUser(employeeEntity, module);
			itemsToPush = itemsToPush.concat(addModules);
		}

		for (let j = 0; j < this.props.selectedServices.length; j++) {
			let service = this.props.selectedServices[j];
			let addService = this.createAccessByUser(employeeEntity, service);
			itemsToPush = itemsToPush.concat(addService);
		}

		return itemsToPush;
	}

	private createAccessByUser(employeeEntity: null | IEmployeeEntity, resource: OptionType): IAccessRelationRawData[] {
		let itemsToPush: IAccessRelationRawData[] = [];

		let roleRefInput = this.rolesInputs.get(resource.value);
		if (!roleRefInput)
			return itemsToPush;

		let values: IRolesInputControlResult = roleRefInput.getValues();

		if (values.roles.length == 0) {
			var exist = this._search(employeeEntity, resource, null);
			if (!exist) {

				const employeFormData = values.valuesPerEmployee[employeeEntity?.id ?? 0];

				itemsToPush.push({
					employee: employeeEntity,
					resource: {
						id: parseInt(resource.value),
						label: resource.label,
						kind: resource.kind,
					},
					role: null,
					from: values.from,
					to: values.to,
					login: employeFormData?.login,
					environment: values.environment,
					password: employeFormData?.password,
					passwordExist: null,
					personAccountDescription: values.personAccountDescription,
					personAccountType: values.personAccountType,
					company: values.company,
					telephone: values.telephone,
					email: values.email,
					location: values.location,
					referenceNumber: values.referenceNumber,
					systemName: values.systemName,

					description: values.description,
					excluded: null,
					errorMessages: null,
				});
			}
		}

		for (let k = 0; k < values.roles.length; k++) {
			let role = values.roles[k];
			var exist = this._search(employeeEntity, resource, role);
			if (!exist) {

				const employeFormData = values.valuesPerEmployee[employeeEntity?.id ?? 0];

				itemsToPush.push({
					employee: employeeEntity,
					resource: {
						id: parseInt(resource.value),
						label: resource.label,
						kind: resource.kind,
					},
					role: role,

					from: values.from,
					to: values.to,

					login: employeFormData?.login,
					environment: values.environment,
					password: employeFormData?.password,
					passwordExist: null,
					personAccountDescription: values.personAccountDescription,
					personAccountType: values.personAccountType,
					company: values.company,
					telephone: values.telephone,
					email: values.email,
					location: values.location,
					referenceNumber: values.referenceNumber,
					systemName: values.systemName,

					description: values.description,
					excluded: null,
					errorMessages: null,
				});
			}
		}

		return itemsToPush;
	}

	private _search(employee: IEmployeeEntity, resource: OptionType, roleData: IRolePerrmission): IAccessRelationData {
		var existRow = this.props.table.searchSingle((r) => {
			let row = r as IAccessRelationData;
			let userAndResource = row.resource.id.toString() == resource.value;

			let employeEquels: boolean;
			if (employee)
				employeEquels = row.employee?.id == employee.id;
			else
				employeEquels = !row.employee;

			let role: boolean;
			if (roleData)
				role = row.role && row.role.id == roleData.id;
			else
				role = !row.role;

			return userAndResource && employeEquels && role;
		});

		return existRow as IAccessRelationData;
	}

	clear() {
		this.props.onChangeSelectedApplications([]);
		this.props.onChangeSelectedServices([]);
		this.setState({
			hasChanges: false,
		})
	}

	isValidAdd(employees: IDictionary<OptionType>) {
		let result = true;
		let state = {
			employeesError: "",
			resourcesError: "",
		};

		if (Object.keys(employees).length == 0 && !this.props.config.fields.employee.hidden) {
			state.employeesError = "control.relation.requireErrorMessage";
			result = false;
		}
		if (this.props.selectedApplications.length == 0
			&& this.props.selectedServices.length == 0) {
			state.resourcesError = "control.relation.requireErrorMessage";
			result = false;
		}
		this.setState(state);

		this.rolesInputs.forEach((v) => {
			if (v) {
				var validationResult = v.isValidAdd();
				result = result && validationResult;
			}
		});

		return result;
	}

	handleWarningAccept() {
		let rows = this.props.table.getRows();
		for (let i = 0; i < rows.length; i++) {
			let row = rows[i];
			row.deleted = true;
		}
		this.props.table.setRows(rows);
		this.setState({
			warningEmployeeChange: null
		});
	}

	handleWarningClose() {
		this.props.onChangeSelectedEmployees([null]);
		
		this.setState({
			warningEmployeeChange: null,
		});
	}

	public handleEmployeeChange(items: OptionType[]) {
		this.props.onChangeSelectedEmployees(items);
		this.setState({
			hasChanges: true,
		});

		if (items.length == 0 || !items[0]) {
			// puste pole
			return;
		}

		if (this.props.config.fields.employee.multiple) {
			// pozwalamy na wielu pracowników
			return;
		}

		let item = items[0];
		let employeId = parseInt(item.value);
		
		let firstOrDefault = this.props.table?.searchSingle(x => x.employee?.id && x.employee.id != employeId);
		if (!firstOrDefault) {
			// we wniosku nie ma innych użytkowników
			return;
		}

		this.setState({
			warningEmployeeChange: item,
		})
	}

	renderUserButtons(employee: OptionType) {
		
		let rowsPerUser: IUserAccessDataRow[] = [];
		let loading = false;
		if (employee) {
			rowsPerUser = this.props.rowsPerUsers[employee.value];
			loading = this.props.loading[employee.value];
		}

		return <>
			{!this.props.hiddenCurrentAccessesButton && <UserAccessesWindow
				rowsPerUsers={rowsPerUser}
				loading={loading}
				user={employee}
				canInsert={true}
				onInsert={(items) => {

					let sensitiveData = items.filter(x => this.openSensitiveDataDialog(x.role));
					let withoutSensitiveData = items.filter(x => !this.openSensitiveDataDialog(x.role));

					this.props.table.push(withoutSensitiveData);
					this.setState({ rowsWithSensitiveData: sensitiveData });
				}}

				config={this.props.currentAccessesTable}
				blockedRelationIds={() => this.props.table?.getRows().filter(x => !x.deleted && x.relationId).map(x => x.relationId) ?? []}
			/>}
			{this.props.currentAuthorizationsButton?.show && <UserAuthorizationsPreviewWindow
				user={employee}
				config={this.props.currentAuthorizationsButton}
			/>}
		</>
	}

	render() {
		let fields = this.props.config.fields;

		let anyResources = !fields.application.hidden || !fields.service.hidden;

		return <>
			<RenderMultiEmployeesInput
				blockChangeEmployees={this.props.blockChangeEmployees}
				config={fields.employee}
				employeesError={this.state.employeesError}
				employeesFilters={this.props.employeesFilters}
				name="selectAccessFor"
				label="accessFor"
				onChange={(v) => this.handleEmployeeChange(v)}
				getButtons={(e) => this.renderUserButtons(e)}
				value={this.props.selectedEmployees}
			/>

			{!this.props.blockInsert && <>
				{this.applicationInputRender()}
				
				<div>
					{this.renderRolesInput()}
				</div>
				{anyResources && <div className="text-align-right">
					<button type="button" className="qd-btn qd-btn-primary" onClick={this.handleAddClick.bind(this)}>
						{lang.get("addScope")}
					</button>
				</div>}
			</>}
			<WarningEmployeeChange
				warningEmployeeChange={this.state.warningEmployeeChange}
				onAccept={this.handleWarningAccept.bind(this)}
				onCancel={this.handleWarningClose.bind(this)}
			/>
		</>
	}

	private handleApplicationsChange(value: OptionType[]) {
		this.setState({ hasChanges: true });
		this.props.onChangeSelectedApplications(value);
	}

	private handleServicesChange(value: OptionType[]) {
		this.setState({ hasChanges: true });
		this.props.onChangeSelectedServices(value);
	}

	private openSensitiveDataDialog(role: IRolePerrmission): boolean {
		let fields = this.props.config.fields;
		return role?.sensitiveData
			&& (!!fields.sensitiveData?.primaryTemplateName || !!fields.sensitiveData?.secondaryTemplateName);
	}

	applicationInputRender() {
		let fields = this.props.config.fields;
		let applicationLabel = lang.getDefaultByPrefix(fields.application, "label", "module");
		let serviceLabel = lang.getDefaultByPrefix(fields.service, "label", "service");

		const applicationGrid = createGridConfig("selectApplication", fields.application.grid, defaultApplicationGrid);
		const serviceGrid = createGridConfig("selectService", fields.service.grid, defaultServiceGrid);

		return <>
			{!fields.application.hidden && <div className="flex-form-field form-field-access-for">
				<div className="flex-field-label"><label>{applicationLabel}:</label></div>
				<div className="flex-field-input" style={{ marginRight: "7px" }}>
					<ErrorMessageWithLabel
						resourcesError={this.state.resourcesError}
						label={applicationLabel}
					/>
					<ResourcesAutocompleteMI
						value={this.props.selectedApplications}
						multiple={fields.application.multiple}

						onSelect={this.handleApplicationsChange.bind(this)}
						kind="Application"
						filters={{
							maxRows: fields.application.maxRows,
							filterId: fields.application.filterId,
						}}
						placeholderText={lang.getDefaultByPrefix(fields.application, "placeholder")}
						grid={applicationGrid}
					/>
				</div>
			</div>}

			{!fields.service.hidden && <div className="flex-form-field form-field-access-for">
				<div className="flex-field-label"><label>{serviceLabel}:</label></div>
				<div className="flex-field-input" style={{ marginRight: "7px" }}>
					<ErrorMessageWithLabel
						resourcesError={this.state.resourcesError}
						label={serviceLabel}
					/>
					<ResourcesAutocompleteMI
						value={this.props.selectedServices}
						multiple={fields.service.multiple}
						onSelect={this.handleServicesChange.bind(this)}
						kind="ServiceAsset"
						filters={{
							maxRows: fields.service.maxRows,
							filterId: fields.service.filterId,
						}}
						placeholderText={lang.getDefaultByPrefix(fields.service, "placeholder")}
						grid={serviceGrid}
					/>
				</div>
			</div>}

			<SensitiveDataAddScopeDialog
				issueId={this.props.issue?.id}
				relations={this.state.rowsWithSensitiveData}
				onAccept={(r) => {
					var result = r as IAccessRelationRawData[];
					this.pushItems(result);
					this.setState({ rowsWithSensitiveData: [] });
				}}
				onClose={() => this.setState({ rowsWithSensitiveData: [] })}
				settings={{
					primaryTemplateName: fields.sensitiveData?.primaryTemplateName,
					secondaryTemplateName: fields.sensitiveData?.secondaryTemplateName
				}}
			/>
		</>;
	}
}